import { createContext, useContext } from 'react';
import type { useSettingsForm } from '@/hooks/useSettingsForm';

export type SettingsContextValue = ReturnType<typeof useSettingsForm> & {
  canClose: boolean;
};

export const SettingsContext = createContext<SettingsContextValue | null>(null);

const useSettingsContext = () => {
  const ctx = useContext(SettingsContext);

  if (!ctx)
    throw new Error('useSettingsContext must be used within SettingsProvider');

  return ctx;
};

export const useSettingsShell = () => {
  const {
    applyChanges,
    formErrors,
    getInputProps,
    getRootProps,
    isLoading,
    profile,
  } = useSettingsContext();

  return {
    applyChanges,
    formErrors,
    getInputProps,
    getRootProps,
    isLoading,
    profile,
  };
};

export const useSettingsHeader = () => {
  const { canClose, handleClose, handleReplayOnboarding } =
    useSettingsContext();

  return { canClose, handleClose, handleReplayOnboarding };
};

export const useScrapersSettings = () => {
  const { mark, scrapers, setScrapers } = useSettingsContext();

  return { mark, scrapers, setScrapers };
};

export const useScoringSettings = () => {
  const {
    mark,
    maxScore,
    maxTailor,
    scoringModel,
    setMaxScore,
    setMaxTailor,
    setScoringModel,
    setTailoringModel,
    setThreshold,
    tailoringModel,
    threshold,
  } = useSettingsContext();

  return {
    mark,
    maxScore,
    maxTailor,
    scoringModel,
    setMaxScore,
    setMaxTailor,
    setScoringModel,
    setTailoringModel,
    setThreshold,
    tailoringModel,
    threshold,
  };
};

export const useScheduleSettings = () => {
  const {
    disableWeekendScans,
    mark,
    nextScanMin,
    offpeakMin,
    peakEndHour,
    peakMin,
    peakStartHour,
    setDisableWeekendScans,
    setOffpeakMin,
    setPeakEndHour,
    setPeakMin,
    setPeakStartHour,
    setTimezone,
    timezone,
  } = useSettingsContext();

  return {
    disableWeekendScans,
    mark,
    nextScanMin,
    offpeakMin,
    peakEndHour,
    peakMin,
    peakStartHour,
    setDisableWeekendScans,
    setOffpeakMin,
    setPeakEndHour,
    setPeakMin,
    setPeakStartHour,
    setTimezone,
    timezone,
  };
};

export const useIntegrationsSettings = () => {
  const {
    discord,
    linkedinCookie,
    mark,
    setDiscord,
    setLinkedinCookie,
    setShowLI,
    showLI,
    threshold,
  } = useSettingsContext();

  return {
    discord,
    linkedinCookie,
    mark,
    setDiscord,
    setLinkedinCookie,
    setShowLI,
    showLI,
    threshold,
  };
};

export const useProfileSettings = () => {
  const {
    experienceYears,
    locationPreference,
    mark,
    minSalary,
    name,
    openFileDialog,
    resumeFileName,
    setExperienceYears,
    setLocationPreference,
    setMinSalary,
    setName,
    setTargetRoles,
    targetRoles,
    uploadResume,
  } = useSettingsContext();

  return {
    experienceYears,
    locationPreference,
    mark,
    minSalary,
    name,
    openFileDialog,
    resumeFileName,
    setExperienceYears,
    setLocationPreference,
    setMinSalary,
    setName,
    setTargetRoles,
    targetRoles,
    uploadResume,
  };
};

export const useApplicationAnswersSettings = () => {
  const {
    applicationAnswers,
    applicationAnswersError,
    cancelEmploymentOverwrite,
    confirmEmploymentOverwrite,
    customAnswerRows,
    employmentHistoryText,
    employmentOverwriteOpen,
    employmentPopulating,
    mark,
    populateEmploymentFromResume,
    referencesText,
    resumeAvailable,
    setApplicationAnswers,
    setCustomAnswerRows,
    setEmploymentHistoryText,
    setReferencesText,
  } = useSettingsContext();

  return {
    answers: applicationAnswers,
    customAnswerRows,
    employmentHistoryText,
    employmentOverwriteOpen,
    employmentPopulating,
    error: applicationAnswersError,
    onCancelEmploymentOverwrite: cancelEmploymentOverwrite,
    onChange: mark(setApplicationAnswers),
    onConfirmEmploymentOverwrite: confirmEmploymentOverwrite,
    onCustomAnswerRowsChange: mark(setCustomAnswerRows),
    onEmploymentHistoryTextChange: mark(setEmploymentHistoryText),
    onPopulateEmployment: populateEmploymentFromResume,
    onReferencesTextChange: mark(setReferencesText),
    referencesText,
    resumeAvailable,
  };
};

export const useSettingsSaveBar = () => {
  const { applyChanges, dirty, lastSavedAgo, resetDefaults, updateProfile } =
    useSettingsContext();

  return { applyChanges, dirty, lastSavedAgo, resetDefaults, updateProfile };
};

export const useSettingsDiscard = () => {
  const { discardOpen, handleCancelDiscard, handleConfirmDiscard } =
    useSettingsContext();

  return { discardOpen, handleCancelDiscard, handleConfirmDiscard };
};
